import { ethers, BigNumber, utils } from "ethers";
import lendingPoolAbi from "../../abi/lendingPool.json";
import wethAbi from "../../abi/weth.json";
import erc20Abi from "../../abi/erc20.json";
import { ALL_TOKENS } from "../constant/externalAddresses";
import {
  showErrorAlert as showAlert,
  showErrorAlert,
  showPendingAlert,
} from "../showAlert";
import { RPC_PROVIDERS } from "../providers";
import { isSupportedChain } from "../constant/chains";
import * as Sentry from "@sentry/react";

export const depositTokenIntoPool = async (
  accountAddress: string,
  lendingPoolAddress: string,
  tokenAddress: string,
  tokenDecimal: number,
  tokenSymbol: string,
  amount: number,
  chainId: number,
  provider: any
) => {
  const depositAmount = utils.parseUnits(amount.toString(), tokenDecimal);

  const signer = provider.getSigner(accountAddress);
  const token = new ethers.Contract(tokenAddress, erc20Abi, signer);
  const pool = new ethers.Contract(lendingPoolAddress, lendingPoolAbi, signer);
  const wethBalance = await token.balanceOf(accountAddress);
  const allowance = await token.allowance(accountAddress, lendingPoolAddress);

  if (wethBalance.lt(depositAmount)) {
    showAlert(`Your ${tokenSymbol} balance is not enough.`);
  } else {
    try {
      if (allowance.lt(depositAmount)) {
        const approveRes = await token.approve(
          lendingPoolAddress,
          depositAmount
        );
        await showPendingAlert(approveRes.hash, `Permission Granted`, provider);
      }

      const res = await pool.deposit(depositAmount);
      await showPendingAlert(
        res.hash,
        `Deposit ${tokenSymbol} Succeed`,
        provider
      );
    } catch (error: any) {
      if (error.message.includes("User denied transaction signature")) {
        showErrorAlert("You denied the transaction.");
      } else {
        showErrorAlert(`Deposit ${tokenSymbol} Failed`);
        Sentry.captureException(error);
      }
    }
  }
};

export const withdrawTokenFromPool = async (
  accountAddress: string,
  lendingPoolAddress: string,
  tokenDecimal: number,
  tokenSymbol: string,
  amount: number,
  provider: any
) => {
  const signer = provider.getSigner(accountAddress);
  const pool = new ethers.Contract(lendingPoolAddress, lendingPoolAbi, signer);
  const exchangeRate = await pool.getEstimatedExchangeRate();
  const userBalance = await pool.balanceOf(accountAddress);
  const withdrawShares = utils
    .parseUnits(amount.toString())
    .mul(BigNumber.from(10).pow(tokenDecimal))
    .div(exchangeRate);

  if (userBalance.lt(withdrawShares)) {
    showAlert("Your balance is not enough.");
  } else {
    try {
      const res = await pool.withdraw(withdrawShares);
      await showPendingAlert(
        res.hash,
        `Withdraw ${tokenSymbol} Succeed`,
        provider
      );
    } catch (e: any) {
      if (e.message.includes("Insufficient Balance")) {
        showErrorAlert("Lending Pool Balance Is Not Enough.");
      } else if (e.message.includes("User denied transaction signature")) {
        showErrorAlert("You denied the transaction.");
      } else {
        showErrorAlert(`Withdraw ${tokenSymbol} Failed`);
        Sentry.captureException(e);
      }
    }
  }
};

export const getPoolCash = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let cashInEth = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const cash = await pool.getCash();
    cashInEth = +ethers.utils.formatEther(cash);
  }
  return cashInEth;
};

export const getTotalBorrowAmount = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let borrowAmountInEth = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const borrowAmount = await pool.totalBorrows();
    borrowAmountInEth = +ethers.utils.formatEther(borrowAmount);
  }
  return borrowAmountInEth;
};

export const getTotalSupplyAmount = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let amountInEth = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const amount = await pool.totalSupply();
    amountInEth = +ethers.utils.formatEther(amount);
  }
  return amountInEth;
};

export const getExchangeRate = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let numInEth = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const num = await pool.getEstimatedExchangeRate();
    numInEth = +ethers.utils.formatEther(num);
  }
  return numInEth;
};

export const getUserPoolShares = async (
  accountAddress: string,
  lendingPoolAddress: string,
  provider: any
) => {
  const pool = new ethers.Contract(
    lendingPoolAddress,
    lendingPoolAbi,
    provider
  );
  const num = await pool.balanceOf(accountAddress);
  const numInEth = ethers.utils.formatEther(num);
  return numInEth;
};

export const getPoolDepositedAmount = async (
  accountAddress: string,
  lendingPoolAddress: string,
  provider: any
) => {
  const filter = {
    address: lendingPoolAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new ethers.utils.Interface(lendingPoolAbi);

  const parsedEvents = res.map((log: any) => {
    return { ...iface.parseLog(log) };
  });

  const userEvents = parsedEvents.filter(
    (e: any) => e.args?.account?.toUpperCase() === accountAddress.toUpperCase()
  );

  const depositEvents = userEvents
    .filter((e: any) => e.name.toUpperCase() === "Deposit".toUpperCase())
    .map((e: any) => {
      return ethers.utils.formatEther(e.args.amountDeposited);
    });

  const sumOfDeposit = depositEvents.reduce(
    (pre: number, curr: string) => pre + +curr,
    0
  );

  return sumOfDeposit;
};

export const getPoolWithdrawnAmount = async (
  accountAddress: string,
  lendingPoolAddress: string,
  provider: any
) => {
  const filter = {
    address: lendingPoolAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new ethers.utils.Interface(lendingPoolAbi);

  const parsedEvents = res.map((log: any) => {
    return { ...iface.parseLog(log) };
  });

  const userEvents = parsedEvents.filter(
    (e: any) => e.args?.account?.toUpperCase() === accountAddress.toUpperCase()
  );

  const withdrawEvents = userEvents
    .filter((e: any) => e.name.toUpperCase() === "Withdraw".toUpperCase())
    .map((e: any) => {
      return ethers.utils.formatEther(e.args.amountWithdrawn);
    });

  const sumOfWithdraw = withdrawEvents.reduce(
    (pre: number, curr: number) => pre + +curr,
    0
  );

  return sumOfWithdraw;
};

export const getLendingPoolLendingRate = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let rate = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const lendingRate = await pool.getLendingRate();
    const numInEth = ethers.utils.formatEther(lendingRate);
    const performanceFee = (await pool.performanceFee()).toString();
    const bps = (await pool.BPS()).toString();
    rate = +numInEth * 60 * 60 * 24 * 365 * (1 - performanceFee / bps);
  }
  return rate;
};

export const getLendingPoolBorrowRate = async (
  lendingPoolAddress: string,
  chainId: number
) => {
  let rate = 0;
  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const pool = new ethers.Contract(
      lendingPoolAddress,
      lendingPoolAbi,
      provider
    );
    const lendingRate = await pool.getBorrowRate();
    const numInEth = ethers.utils.formatEther(lendingRate);
    rate = +numInEth * 60 * 60 * 24 * 365;
  }
  return rate;
};

export const getAccountWETHBalance = async (
  accountAddress: string,
  chainId: number,
  provider: any
) => {
  let wethBalance = 0;

  const signer = provider.getSigner(accountAddress);
  const weth = new ethers.Contract(ALL_TOKENS[chainId].WETH, wethAbi, signer);

  try {
    wethBalance = +ethers.utils.formatEther(
      await weth.balanceOf(accountAddress)
    );
  } catch (e: any) {}

  return wethBalance;
};

export const wrapETHtoWETH = async (
  accountAddress: string,
  amount: number,
  chainId: number,
  provider: any
) => {
  const ethAmount = ethers.utils.parseEther(amount.toString());
  const signer = provider.getSigner(accountAddress);
  const weth = new ethers.Contract(ALL_TOKENS[chainId].WETH, wethAbi, signer);
  const ethBalance = await provider.getBalance(accountAddress);

  if (ethBalance.lt(ethAmount)) {
    showAlert("Your ETH balance is not enough.");
  } else {
    try {
      const res = await weth.deposit({ value: ethAmount });
      await showPendingAlert(res.hash, "Wrap ETH to WETH Succeed", provider);
    } catch (e: any) {
      if (e.message.includes("User denied transaction signature")) {
        showErrorAlert("You denied the transaction.");
      } else {
        showErrorAlert("Wrap ETH Failed");
        Sentry.captureException(e);
      }
    }
  }
};

export const getAccountETHBalance = async (
  accountAddress: string,
  chainId: number,
  provider: any
) => {
  let ethBalance = 0;

  const eth = await provider.getBalance(accountAddress);

  ethBalance = +ethers.utils.formatEther(eth);

  return ethBalance;
};

export const getUserBorrowAmountFromLogs = async (
  lendingPoolAddress: string,
  borrowId: number,
  borrowTokenDecimal: number,
  provider: any
) => {
  const filter = {
    address: lendingPoolAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new ethers.utils.Interface(lendingPoolAbi);

  const parsedEvents = res.map((log: any) => {
    return { ...iface.parseLog(log) };
  });

  const borrowEvents = parsedEvents.filter(
    (e: any) =>
      e.name.toUpperCase() === "BORROW" &&
      +e.args.borrowId.toString() === borrowId
  );

  return +ethers.utils.formatUnits(
    borrowEvents[0].args.borrowAmount,
    borrowTokenDecimal
  );
};
